import { useCallback, useEffect } from "react";
import { useAuth } from "@workspace/ui/hooks";
import { sweepAllPendingApps } from "@workspace/shared";

// True while an embedded practice app is mounted (RoleHost stamps this class).
// While an app is live, ITS CurrentGameSync owns syncing — the sweeper stands
// down to avoid two things touching the shared app-key header / current game.
const appMounted = () =>
  typeof document !== "undefined" &&
  document.body.classList.contains("dashboard-role-active");

/**
 * Dashboard-level cross-app pending-play sync. Runs only on the dashboard (no
 * app mounted) and uploads EVERY app's pending plays, so a coach who tagged
 * plays offline in several apps gets them all synced without re-opening each —
 * just by coming back online to the dashboard.
 *
 * Renders nothing. Mount once inside AuthProvider.
 */
export function GlobalPendingSync(): null {
  const { isAuthenticated } = useAuth();

  const sweep = useCallback(() => {
    if (appMounted()) return;
    if (typeof navigator !== "undefined" && !navigator.onLine) return;
    if (!isAuthenticated) return;
    void sweepAllPendingApps();
  }, [isAuthenticated]);

  // On mount / auth change / returning to the dashboard.
  useEffect(() => {
    sweep();
  }, [sweep]);

  // On reconnect, on tab-focus, and on a 60s idle tick.
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    const onOnline = () => sweep();
    const onVisible = () => {
      if (document.visibilityState === "visible") sweep();
    };
    const id = window.setInterval(sweep, 60_000);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [sweep]);

  return null;
}
